import {
  ComponentRef,
  DestroyRef,
  Directive,
  ViewContainerRef,
  effect,
  inject,
  input,
  untracked,
} from '@angular/core';
import { outputToObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs';

import { FeatureHighlightBadgeComponent } from './feature-highlight-badge.component';
import { FeatureHighlightKey } from './feature-highlight.config';
import { FeatureHighlightConfig } from './feature-highlight.model';
import { FeatureHighlightService } from './feature-highlight.service';

@Directive({
  selector: '[diFeatureHighlight]',
})
export class FeatureHighlightDirective {
  readonly diFeatureHighlight = input.required<FeatureHighlightKey>();

  private readonly service = inject(FeatureHighlightService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);

  private badgeRef: ComponentRef<FeatureHighlightBadgeComponent> | null = null;

  constructor() {
    effect(() => {
      const key = this.diFeatureHighlight();
      const config = this.service.getConfig(key);
      untracked(() => {
        if (config && !this.badgeRef) {
          this.showBadge(key, config);
        } else if (!config && this.badgeRef) {
          this.removeBadge();
        }
      });
    });
  }

  private showBadge(key: FeatureHighlightKey, config: FeatureHighlightConfig): void {
    this.badgeRef = this.viewContainerRef.createComponent(FeatureHighlightBadgeComponent);
    this.badgeRef.setInput('config', config);

    outputToObservable(this.badgeRef.instance.dismissed)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.service.dismiss(key);
        this.removeBadge();
      });
  }

  private removeBadge(): void {
    if (this.badgeRef) {
      this.badgeRef.destroy();
      this.badgeRef = null;
    }
  }
}
